import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import { Link } from "react-router-dom";
import countries from "@/constants/array/country_city.json";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import VirtualizedCitySelect from "@/components/ui/virtualized-city-select";
import { LoaderButton } from "@/components/ui/loader-button";

const RedemptionMerchantCreate = () => {
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedWarehouseCountry, setSelectedWarehouseCountry] =
    useState(null);
  const [contactDetails, setContactDetails] = useState([
    { name: "", email: "", role: "", phoneNumber: "" },
  ]);
  const [warehouseDetails, setWarehouseDetails] = useState([
    { warehouseName: "", email: "", address: "", country: "", city: "" },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    resolver: yupResolver(RedemptionMerchantSchema),
    defaultValues: {
      merchantName: "",
      merchantCode: "",
      merchantDescription: "",
      website: "",
      email: "",
      address: "",
      country: "",
      city: "",
      termsAndConditions: "",
      howToUse: "",
      shippingProfile: "",
    },
  });

  const onSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      // 1. Create Redemption Merchant
      const countryObj =
        selectedCountry ||
        (formData.country ? JSON.parse(formData.country) : null);
      const merchantPayload = {
        merchantCode: formData.merchantCode,
        merchantName: formData.merchantName,
        merchantEmail: formData.email,
        merchantAddress: formData.address,
        howToUseTemplate: formData.howToUse,
        merchantWebsiteUrl: formData.website,
        merchantDescription: formData.merchantDescription,
        termsAndConditionsTemplate: formData.termsAndConditions,
        city: formData.city,
        countryVO: countryObj
          ? { code: countryObj.code, name: countryObj.name }
          : undefined,
      };
      const merchantRes = await fetch(
        `${
          import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL
        }/redemptionMerchant/createOrUpdateRedemptionMerchant`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(merchantPayload),
        }
      );
      if (!merchantRes.ok) throw new Error("Failed to create merchant");
      const merchantData = await merchantRes.json();
      const merchantId =
        merchantData?.id ||
        merchantData?.redemptionMerchantId ||
        merchantData?.data?.id;
      if (!merchantId) throw new Error("Merchant ID not returned");

      // 2. Create Contacts
      const contactsPayload = contactDetails.map((c) => ({
        contactName: c.name,
        contactEmail: c.email,
        contactRole: c.role,
        contactPhoneNumber: c.phoneNumber,
        redemptionMerchantId: merchantId,
      }));
      if (contactsPayload.length > 0) {
        const contactsRes = await fetch(
          `${
            import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL
          }/redemptionMerchant/createOrUpdateRedemptionMerchantContact`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(contactsPayload),
          }
        );
        if (!contactsRes.ok) throw new Error("Failed to create contacts");
      }

      // 3. Create Warehouses
      const warehousesPayload = warehouseDetails.map((w, idx) => {
        let countryObj = w.country ? JSON.parse(w.country) : null;
        console.log(countryObj, "countryObj---");
        return {
          name: w.warehouseName,
          address: w.address,
          email: w.email,
          city: w.city,
          countryVO: countryObj
            ? { code: countryObj.iso3, name: countryObj.name }
            : undefined,
          redemptionMerchantId: merchantId,
          isPrimary: idx === 0,
        };
      });
      if (warehousesPayload.length > 0) {
        const warehousesRes = await fetch(
          `${
            import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL
          }/redemptionMerchant/createOrUpdateRedemptionMerchantWarehouse`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(warehousesPayload),
          }
        );
        if (!warehousesRes.ok) throw new Error("Failed to create warehouses");
      }

      toast.success("Redemption Merchant created successfully");
      navigate(`/rewardpoint/redemption-merchants`);
    } catch (error) {
      toast.error(error?.message || "Failed to create merchant");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Contact Details handlers
  const handleContactChange = (idx, field, value) => {
    setContactDetails((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c))
    );
  };
  const addContact = () =>
    setContactDetails((prev) => [
      ...prev,
      { name: "", email: "", role: "", phoneNumber: "" },
    ]);
  const removeContact = (idx) =>
    setContactDetails((prev) => prev.filter((_, i) => i !== idx));

  // Warehouse Details handlers
  const handleWarehouseChange = (idx, field, value) => {
    setWarehouseDetails((prev) =>
      prev.map((w, i) => (i === idx ? { ...w, [field]: value } : w))
    );
  };
  const addWarehouse = () =>
    setWarehouseDetails((prev) => [
      ...prev,
      { warehouseName: "", email: "", address: "", country: "", city: "" },
    ]);
  const removeWarehouse = (idx) =>
    setWarehouseDetails((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" asChild className="bg-white">
          <Link to="/rewardpoint/redemption-merchants">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Add Redemption Merchant
          </h1>
          <p className="text-gray-500">Create a new redemption merchant</p>
        </div>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Basic Information */}
            <Card className="border-0 shadow-sm overflow-hidden bg-white">
              <CardHeader className="card-header-compact bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <FormField
                  control={form.control}
                  name="merchantName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Merchant Name*</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter merchant name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="merchantCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Merchant Code*</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter merchant code" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="merchantDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Merchant Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Enter merchant description"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="website"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Website</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter website url" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter merchant email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Merchant Address</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter address" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          const selected = JSON.parse(value);
                          setSelectedCountry(selected);
                          field.onChange(value);
                          // Reset city when country changes
                          form.setValue("city", "");
                        }}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Country" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {(countries as any)?.map((country) => (
                            <SelectItem
                              key={country?.name}
                              value={JSON.stringify(country)}
                            >
                              {country?.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <VirtualizedCitySelect
                      cities={selectedCountry?.cities || []}
                      value={field.value}
                      onValueChange={field.onChange}
                      placeholder="Select City"
                      disabled={!selectedCountry?.cities?.length}
                      label="City"
                    />
                  )}
                />
                <FormField
                  control={form.control}
                  name="termsAndConditions"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Terms & Conditions</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Enter terms & conditions"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="howToUse"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>How To Use</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Enter how to use" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
            {/* Contact Details & Warehouse Details */}
            <div className="space-y-8">
              {/* Contact Details */}
              <Card className="border-0 shadow-sm overflow-hidden bg-white">
                <CardHeader className="card-header-compact bg-gray-50">
                  <CardTitle className="text-lg font-medium text-gray-800">
                    Contact Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  {contactDetails.map((contact, idx) => (
                    <div
                      key={idx}
                      className="border-b pb-4 mb-4 last:border-b-0 last:pb-0 last:mb-0"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold">
                          Contact Detail {idx + 1}
                        </span>
                        {contactDetails.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeContact(idx)}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          placeholder="Name*"
                          value={contact.name}
                          onChange={(e) =>
                            handleContactChange(idx, "name", e.target.value)
                          }
                        />
                        <Input
                          placeholder="Email*"
                          value={contact.email}
                          onChange={(e) =>
                            handleContactChange(idx, "email", e.target.value)
                          }
                        />
                        <Input
                          placeholder="Role*"
                          value={contact.role}
                          onChange={(e) =>
                            handleContactChange(idx, "role", e.target.value)
                          }
                        />
                        <Input
                          placeholder="Phone Number*"
                          value={contact.phoneNumber}
                          onChange={(e) =>
                            handleContactChange(
                              idx,
                              "phoneNumber",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addContact}
                  >
                    + Add Contact
                  </Button>
                </CardContent>
              </Card>
              {/* Delivery Warehouse Details */}
              <Card className="border-0 shadow-sm overflow-hidden bg-white">
                <CardHeader className="card-header-compact bg-gray-50">
                  <CardTitle className="text-lg font-medium text-gray-800">
                    Delivery Warehouse Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  {warehouseDetails.map((warehouse, idx) => (
                    <div
                      key={idx}
                      className="border-b pb-4 mb-4 last:border-b-0 last:pb-0 last:mb-0"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold">
                          Warehouse - {idx + 1}
                          {idx === 0 && " (Primary)"}
                        </span>
                        {warehouseDetails.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeWarehouse(idx)}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          placeholder="Warehouse Name*"
                          value={warehouse.warehouseName}
                          onChange={(e) =>
                            handleWarehouseChange(
                              idx,
                              "warehouseName",
                              e.target.value
                            )
                          }
                        />
                        <Input
                          placeholder="Email*"
                          value={warehouse.email}
                          onChange={(e) =>
                            handleWarehouseChange(idx, "email", e.target.value)
                          }
                        />
                        <Input
                          placeholder="Address*"
                          value={warehouse.address}
                          onChange={(e) =>
                            handleWarehouseChange(
                              idx,
                              "address",
                              e.target.value
                            )
                          }
                        />
                        <Select
                          onValueChange={(value) => {
                            const selected = JSON.parse(value);
                            // Set country for this warehouse
                            handleWarehouseChange(idx, "country", value);
                            // Reset city for this warehouse
                            handleWarehouseChange(idx, "city", "");
                            setSelectedWarehouseCountry(selected);
                          }}
                          value={warehouse.country}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a country" />
                          </SelectTrigger>
                          <SelectContent>
                            {(countries as any)?.map((country) => (
                              <SelectItem
                                key={country?.name}
                                value={JSON.stringify(country)}
                              >
                                {country?.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <VirtualizedCitySelect
                          cities={(() => {
                            try {
                              return warehouse.country
                                ? JSON.parse(warehouse.country)?.cities || []
                                : [];
                            } catch {
                              return [];
                            }
                          })()}
                          value={warehouse.city}
                          onValueChange={(value) =>
                            handleWarehouseChange(idx, "city", value)
                          }
                          placeholder="Choose a city"
                          disabled={
                            !warehouse.country ||
                            !(() => {
                              try {
                                return JSON.parse(warehouse.country)?.cities
                                  ?.length;
                              } catch {
                                return false;
                              }
                            })()
                          }
                          label="City"
                          error={undefined}
                        />
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addWarehouse}
                  >
                    + Add Warehouse
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
          <Separator className="my-8" />
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/rewardpoint/redemption-merchants")}
            >
              Cancel
            </Button>
            <LoaderButton type="submit" loading={isSubmitting}>
              <Save className="h-4 w-4 mr-2" /> Create Merchant
            </LoaderButton>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default RedemptionMerchantCreate;

const RedemptionMerchantSchema = Yup.object().shape({
  merchantName: Yup.string().required("Merchant Name is required"),
  merchantCode: Yup.string().required("Merchant Code is required"),
  merchantDescription: Yup.string(),
  website: Yup.string().url("Website must be a valid URL"),
  email: Yup.string().email("Email must be valid"),
  address: Yup.string(),
  country: Yup.string(),
  city: Yup.string(),
  termsAndConditions: Yup.string(),
  howToUse: Yup.string(),
  shippingProfile: Yup.string(),
});
